import type {
  IApprovalBookingConflict,
  IBulkBookingsPanelLabelKeys,
} from "../models";
import { ComputedDataKey } from "./common.const";

export const SHOWN_PASSENGER_NAMES_LENGTH = 6;
export const BOOKING_DIRECTIONS_MULTIPLIER = 2;

export const BOOKING_FILTERS = {
  tags: "metadata.tags",
  shiftTime: "metadata.shiftTime",
  originAreaId: "metadata.originAreaId",
  destinationAreaId: "metadata.destinationAreaId",
  type: "metadata.type",
  passengersHomeAreaId: "metadata.passengersHomeAreaId",
  passengersDestinationAreaId: "metadata.passengersDestinationAreaId",
} as const;

// Columns accepted by the bookings file upload. The keys are the API field names the
// backend expects (BulkPrepareBookingDto) and double as the English header names; the
// localized header text lives in the translations under BOOKING_UPLOAD_COLUMNS_KEY, so a
// customer wording change stays a translations-only change.
export const BOOKING_UPLOAD_COLUMNS = {
  worker: "worker",
  date: "date",
  collect: "collect",
  distribute: "distribute",
  from: "from",
  to: "to",
  shift: "shift",
} as const;

export const BOOKING_UPLOAD_COLUMNS_KEY = "booking.bulk.uploadColumns";

export const BOOKING_BULK_CREATE_PANEL_LABEL_KEYS: IBulkBookingsPanelLabelKeys =
  {
    commit: "general.CREATE",
    resultPending: "booking.bulk.readyToBook",
    resultFinished: "booking.bulk.successfullyBooked",
    progressFinished: "rideProduction.creationFinished",
    progressError: "rideProduction.creationBookingsError",
    progressRunning: "rideProduction.creationProgress",
    conflictTitles: {},
  };

/** The bulk date/time panel edits bookings rather than creating them, so it says so throughout. */
export const BOOKING_DATE_TIME_UPDATE_PANEL_LABEL_KEYS: IBulkBookingsPanelLabelKeys =
  {
    commit: "general.UPDATE",
    resultPending: "booking.bulk.readyToUpdate",
    resultFinished: "booking.bulk.successfullyUpdated",
    progressFinished: "booking.bulk.updateFinished",
    progressError: "errors.errorUpdatingBookingsDateTime",
    progressRunning: "booking.bulk.updateProgress",
    // Written out rather than computed from `BookingConflictErrorType`: this module is imported by
    // the models it would read that enum from, and a value import here closes the cycle. The
    // `Record` type still rejects a key that is not a conflict error type.
    conflictTitles: {
      gapSingleOverlappingBookingAToA:
        "booking.bulk.dateTimeConflictTitles.gapSingleOverlappingBookingAToA",
      gapSingleOverlappingBookingBToA:
        "booking.bulk.dateTimeConflictTitles.gapSingleOverlappingBookingBToA",
      gapSingleOverlappingBookingBToB:
        "booking.bulk.dateTimeConflictTitles.gapSingleOverlappingBookingBToB",
    },
  };

interface IBulkCreateBookingConflictCsvHeader {
  label: string;
  key: keyof IApprovalBookingConflict;
}

export const BULK_CREATE_BOOKING_CONFLICT_CSV_HEADERS: IBulkCreateBookingConflictCsvHeader[] =
  [
    {
      label: "Passenger ID",
      key: "passengerId",
    },
    {
      label: "Passenger name",
      key: "passengerFullName",
    },
    {
      label: "Date",
      key: "date",
    },
    {
      label: "Error type",
      key: "errorType",
    },
    {
      label: "Reason",
      key: "approvalReason",
    },
  ];
export const BULK_OPERATIONS_BOOKINGS_CSV_HEADERS = [
  {
    label: "Code",
    key: "code",
  },
  {
    label: "Name",
    key: "name",
  },
  {
    label: "Phone",
    key: "passenger.phoneMobile",
  },
  {
    label: "Unit",
    key: "unitName",
  },
  {
    label: "Remarks",
    key: "remarks",
  },
  {
    label: "Date",
    key: "date",
  },
  {
    label: "Ways",
    key: "direction",
  },
  {
    label: "Shift",
    key: "shiftId",
  },
  {
    label: "From",
    key: "originStationName",
  },
  {
    label: "To",
    key: "destinationStationId",
  },
  {
    label: "Seats",
    key: "seats",
  },
  {
    label: "Created",
    key: "createdAt",
  },
  {
    label: "Updated",
    key: "updatedAt",
  },
];

export const FULL_LOAD_BULK_SET_BOOKINGS_PERCENT = 100;
export const BOOKING_CALENDAR_COLUMN_WIDTH = 230;
export const BOOKING_CALENDAR_HEADER_ROW_HEIGHT = 40;
export const BOOKING_CALENDAR_ROW_HEIGHT = 100;

export enum BookingComputedDataKeys {
  hubDisplay = "hubDisplay",
}

export const BookingComputedFieldNames = {
  hubDisplay: `${ComputedDataKey}.${BookingComputedDataKeys.hubDisplay}`,
} as const;
